import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useParams, Link, Navigate } from 'react-router-dom';
import { blogPosts, legacyBlogSlugs } from '../shared/blogPosts';
import { mermaidDiagrams } from '../shared/mermaidDiagrams';
import mermaid from 'mermaid';
import SessionReplay from '../components/SessionReplay';
import ThinkWakeFigure from '../components/ThinkWakeFigure';
import McpVsCliFigure from '../components/McpVsCliFigure';
import BlenderQuickstartFigure from '../components/BlenderQuickstartFigure';
import AppControlCeilingFigure from '../components/AppControlCeilingFigure';
import AgentProcessFigure from '../components/AgentProcessFigure';
import SystemOutputFigure from '../components/SystemOutputFigure';
import PiperCompactionFigure from '../components/PiperCompactionFigure';
import StatelessReliabilityFigure from '../components/StatelessReliabilityFigure';
import { statelessDemoTrace } from '../shared/statelessDemoTrace';
import usePageMeta from '../utils/usePageMeta';
import VideoFigure from '../components/VideoFigure';

const XPostEmbed: React.FC<{ url: string; caption?: string }> = ({ url, caption }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const loadEmbed = () => {
      const widgets = (window as any).twttr?.widgets;
      if (widgets && containerRef.current) {
        widgets.load(containerRef.current);
      }
    };

    let script = document.querySelector<HTMLScriptElement>(
      'script[src="https://platform.twitter.com/widgets.js"]'
    );

    if (!script) {
      script = document.createElement('script');
      script.src = 'https://platform.twitter.com/widgets.js';
      script.async = true;
      script.charset = 'utf-8';
      document.head.appendChild(script);
    }

    script.addEventListener('load', loadEmbed);
    loadEmbed();

    return () => script?.removeEventListener('load', loadEmbed);
  }, [url]);

  return (
    <motion.div
      ref={containerRef}
      className="x-post-embed"
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
    >
      <blockquote
        className="twitter-tweet"
        data-align="center"
        data-conversation="none"
        data-dnt="true"
        data-theme="light"
      >
        <a href={url} target="_blank" rel="noopener noreferrer">
          View this post on X
        </a>
      </blockquote>
      {caption && <p className="x-post-embed__caption">{caption}</p>}
    </motion.div>
  );
};

// Component to render Mermaid diagrams as images
const MermaidDiagram: React.FC<{ id: string; definition: string; description?: string }> = ({ id, definition, description }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [svgContent, setSvgContent] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [isRendering, setIsRendering] = React.useState(true);

  useEffect(() => {
    const renderDiagram = async () => {
      if (!definition) return;

      setError(null);
      setIsRendering(true);

      try {
        // Wait a bit to ensure Mermaid is initialized
        await new Promise(resolve => setTimeout(resolve, 200));

        // Render the diagram - mermaid.render returns { svg, bindFunctions }
        const result = await mermaid.render(id, definition);
        
        if (result && result.svg) {
          setSvgContent(result.svg);
          setIsRendering(false);
        } else {
          throw new Error('No SVG returned from Mermaid');
        }
      } catch (err: any) {
        console.error('Error rendering Mermaid diagram:', err);
        setError(err?.message || 'Failed to render diagram');
        setIsRendering(false);
      }
    };

    renderDiagram();
  }, [id, definition]);

  // Scale the SVG after it's rendered and add borders to arrows for architecture diagram
  useEffect(() => {
    if (containerRef.current && svgContent) {
      const svg = containerRef.current.querySelector('svg');
      if (svg) {
        svg.style.width = '100%';
        svg.style.height = 'auto';
        svg.style.maxWidth = '100%';
        
        // Add border to arrows for the architecture diagram using drop shadow filter
        if (id.includes('assistant-architecture-2')) {
          // Create filter for border effect if it doesn't exist
          let defs = svg.querySelector('defs');
          if (!defs) {
            defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
            svg.insertBefore(defs, svg.firstChild);
          }
          
          if (!defs.querySelector('#arrow-border')) {
            const filter = document.createElementNS('http://www.w3.org/2000/svg', 'filter');
            filter.id = 'arrow-border';
            filter.setAttribute('x', '-50%');
            filter.setAttribute('y', '-50%');
            filter.setAttribute('width', '200%');
            filter.setAttribute('height', '200%');
            
            // Create border using multiple feMorphology and feFlood
            const feMorphology = document.createElementNS('http://www.w3.org/2000/svg', 'feMorphology');
            feMorphology.setAttribute('operator', 'dilate');
            feMorphology.setAttribute('radius', '1.5');
            feMorphology.setAttribute('in', 'SourceAlpha');
            feMorphology.setAttribute('result', 'thicken');
            
            const feFlood = document.createElementNS('http://www.w3.org/2000/svg', 'feFlood');
            feFlood.setAttribute('flood-color', '#000000');
            feFlood.setAttribute('flood-opacity', '0.8');
            feFlood.setAttribute('result', 'border');
            
            const feComposite1 = document.createElementNS('http://www.w3.org/2000/svg', 'feComposite');
            feComposite1.setAttribute('in', 'border');
            feComposite1.setAttribute('in2', 'thicken');
            feComposite1.setAttribute('operator', 'in');
            feComposite1.setAttribute('result', 'borderFill');
            
            const feMerge = document.createElementNS('http://www.w3.org/2000/svg', 'feMerge');
            const feMergeNode1 = document.createElementNS('http://www.w3.org/2000/svg', 'feMergeNode');
            feMergeNode1.setAttribute('in', 'borderFill');
            const feMergeNode2 = document.createElementNS('http://www.w3.org/2000/svg', 'feMergeNode');
            feMergeNode2.setAttribute('in', 'SourceGraphic');
            feMerge.appendChild(feMergeNode1);
            feMerge.appendChild(feMergeNode2);
            
            filter.appendChild(feMorphology);
            filter.appendChild(feFlood);
            filter.appendChild(feComposite1);
            filter.appendChild(feMerge);
            defs.appendChild(filter);
          }
          
          // Apply filter to all arrow paths
          const paths = svg.querySelectorAll('path[marker-end], path.flowchart-link');
          paths.forEach((path) => {
            (path as SVGPathElement).setAttribute('filter', 'url(#arrow-border)');
          });
        }
      }
    }
  }, [svgContent, id]);

  if (error) {
    return (
      <div className="my-6 p-4 bg-red-900/20 border border-red-500 rounded">
        <p className="text-red-500 text-sm">Error rendering diagram: {error}</p>
        <details className="mt-2">
          <summary className="text-xs text-minimal-grey-dark cursor-pointer">Show definition</summary>
          <pre className="text-xs mt-2 text-minimal-grey-dark overflow-auto whitespace-pre-wrap max-h-40">{definition}</pre>
        </details>
      </div>
    );
  }

  if (isRendering) {
    return (
      <div className="my-6 flex justify-center">
        <div className="text-minimal-grey-dark text-sm">Rendering diagram...</div>
      </div>
    );
  }

  if (!svgContent) {
    return null;
  }

  return (
    <div className="mermaid-figure-breakout">
      <motion.figure
        className="mermaid-figure"
        initial={{ opacity: 0, y: 18 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="mermaid-figure__viewport">
          <div
            ref={containerRef}
            className="mermaid-diagram"
            dangerouslySetInnerHTML={{ __html: svgContent || '' }}
          />
        </div>
        {description && (
          <figcaption className="mermaid-figure__caption">
            {description}
          </figcaption>
        )}
      </motion.figure>
    </div>
  );
};

// Initialize Mermaid once globally
let mermaidInitialized = false;

/** First couple of sentences of body copy, stripped of markup, for meta tags. */
const excerpt = (content: string, limit = 180): string => {
  const plain = content
    .replace(/\[[A-Z-]+:[^\]]*\]/g, ' ')
    .replace(/^##.*$/gm, ' ')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/[*_`>]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

  if (plain.length <= limit) {
    return plain;
  }

  const cut = plain.slice(0, limit);
  return `${cut.slice(0, cut.lastIndexOf(' '))}…`;
};

const BlogDetail: React.FC = () => {
  const { blogSlug } = useParams<{ blogSlug: string }>();
  const post = blogPosts.find(p => p.slug === blogSlug);

  usePageMeta({
    title: post?.title ?? 'Writing',
    description: post ? post.subtitle || excerpt(post.content) : '',
    path: post ? `/${post.slug}` : '/work',
    image: post?.heroImage,
    type: 'article',
    publishedTime: post?.date,
  });

  // Initialize Mermaid (must be before early return)
  useEffect(() => {
    if (!mermaidInitialized) {
      mermaid.initialize({ 
        startOnLoad: false,
        theme: 'dark',
        themeVariables: {
          primaryColor: '#1e3a5f',
          primaryTextColor: '#f5f5f5',
          primaryBorderColor: '#1565c0',
          lineColor: '#424242',
          secondaryColor: '#512da8',
          tertiaryColor: '#d84315'
        }
      });
      mermaidInitialized = true;
    }
  }, []);

  if (!post) {
    const renamed = blogSlug ? legacyBlogSlugs[blogSlug] : undefined;
    return <Navigate to={renamed ? `/${renamed}` : '/404'} replace />;
  }

  const formatDate = (dateString: string) => {
    // Parse date-only values at local noon so time zones cannot shift the day.
    const date = new Date(`${dateString}T12:00:00`);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const calculateReadTime = (content: string): number => {
    // Average reading speed is about 200-250 words per minute, using 225 as average
    const words = content.trim().split(/\s+/).length;
    const readTime = Math.ceil(words / 225);
    return readTime;
  };

  const readTime = post.readTime ?? calculateReadTime(post.content);

  // Parse content and insert Mermaid diagrams if specified
  const parseContent = (content: string, diagramConfig?: string | Array<{ key: string; insertAfter: string }>) => {
    const segments: Array<{ type: 'text' | 'mermaid'; content: string; id?: string; description?: string }> = [];
    
    if (!diagramConfig) {
      segments.push({ type: 'text', content });
      return segments;
    }

    const lines = content.split('\n');
    const insertionPoints: Array<{ index: number; diagramKey: string }> = [];
    
    // Handle single diagram (backward compatibility)
    if (typeof diagramConfig === 'string') {
      const keyword = 'rearchitected'; // Default keyword for single diagram
      for (let i = 0; i < lines.length; i++) {
        if (lines[i].includes(keyword)) {
          for (let j = i + 1; j <= lines.length; j++) {
            if (j === lines.length || lines[j].trim() === '') {
              insertionPoints.push({ index: j, diagramKey: diagramConfig });
              break;
            }
          }
          break;
        }
      }
    } else {
      // Handle multiple diagrams
      for (const diagram of diagramConfig) {
        for (let i = 0; i < lines.length; i++) {
          if (lines[i].toLowerCase().includes(diagram.insertAfter.toLowerCase())) {
            // Find the end of this paragraph (next empty line or end of content)
            for (let j = i + 1; j <= lines.length; j++) {
              if (j === lines.length || lines[j].trim() === '') {
                insertionPoints.push({ index: j, diagramKey: diagram.key });
                break;
              }
            }
            break;
          }
        }
      }
    }
    
    // Sort insertion points by index (descending) to insert from end to start
    insertionPoints.sort((a, b) => b.index - a.index);
    
    if (insertionPoints.length > 0) {
      let currentContent = content;
      
      // Insert diagrams from end to start to preserve indices
      for (const point of insertionPoints) {
        const linesArray = currentContent.split('\n');
        const beforeContent = linesArray.slice(0, point.index).join('\n');
        const afterContent = linesArray.slice(point.index).join('\n');
        
        const diagram = mermaidDiagrams[point.diagramKey];
        if (diagram) {
          currentContent = beforeContent + '\n\n[MERMAID:' + point.diagramKey + ']\n\n' + afterContent;
        }
      }
      
      // Now parse the content with markers
      const parts = currentContent.split(/\n\n\[MERMAID:([^\]]+)\]\n\n/);
      
      for (let i = 0; i < parts.length; i++) {
        if (i % 2 === 0) {
          // Text content
          if (parts[i].trim()) {
            segments.push({ type: 'text', content: parts[i] });
          }
        } else {
          // Diagram marker
          const diagramKey = parts[i];
          if (mermaidDiagrams[diagramKey]) {
            const diagram = mermaidDiagrams[diagramKey];
            segments.push({
              type: 'mermaid',
              content: typeof diagram === 'string' ? diagram : diagram.definition,
              id: `mermaid-${post.slug}-${diagramKey}-${Date.now()}-${i}`,
              description: typeof diagram === 'string' ? undefined : diagram.description
            });
          }
        }
      }
    } else {
      // No insertion points found, just return content as text
      segments.push({ type: 'text', content });
    }

    return segments;
  };

  const contentSegments = parseContent(post.content, post.mermaidDiagrams || post.mermaidDiagram);

  const toHeadingId = (heading: string) =>
    `section-${heading
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')}`;

  const sectionHeadings = contentSegments
    .filter(segment => segment.type === 'text')
    .flatMap(segment => segment.content.split('\n'))
    .map(line => line.trim())
    // Only top-level sections reach the table of contents; ### subsections do not.
    .filter(line => line.startsWith('##') && !line.startsWith('###'))
    .map(line => line.substring(2).trim())
    .filter((heading, index, headings) => headings.indexOf(heading) === index);

  const tableOfContents = [
    { id: 'introduction', label: 'Introduction' },
    ...sectionHeadings.map(heading => ({
      id: toHeadingId(heading),
      label: heading,
    })),
  ];

  // Company mapping: blog text -> { logo, slug }
  const companyMap: Record<string, { logo: string; slug: string }> = {
    'IrisGo': { logo: '/irisgo.png', slug: 'irisgo' },
    'The Innovation Story': { logo: '/TIS.png', slug: 'the-innovation-story' },
    'Electron Online': { logo: '/electron.jpg', slug: 'electron-online' },
    'GOQii': { logo: '/GOQii.png', slug: 'goqii' },
    'Manulife': { logo: '/manulife.svg', slug: 'manulife' },
    'Scitara': { logo: '/Scitara.png', slug: 'scitara-technologies' },
  };

  // Helper function to render text with embedded links and company logos
  const renderTextWithLinks = (text: string) => {
    // First, handle markdown-style links: [text](url)
    const linkPattern = /\[([^\]]+)\]\(([^)]+)\)/g;
    const parts: Array<{ type: 'text' | 'link'; content: string; url?: string }> = [];
    let lastIndex = 0;
    let match;
    let hasLinks = false;

    while ((match = linkPattern.exec(text)) !== null) {
      hasLinks = true;
      // Add text before the link
      if (match.index > lastIndex) {
        parts.push({ type: 'text', content: text.substring(lastIndex, match.index) });
      }
      // Add the link
      parts.push({ type: 'link', content: match[1], url: match[2] });
      lastIndex = match.index + match[0].length;
    }
    // Add remaining text
    if (lastIndex < text.length) {
      parts.push({ type: 'text', content: text.substring(lastIndex) });
    }

    // If no links found, just return the text with company logos
    if (!hasLinks) {
      const companyNames = Object.keys(companyMap).sort((a, b) => b.length - a.length);
      const companyPattern = new RegExp(`(${companyNames.map(name => name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'gi');
      const textParts = text.split(companyPattern);
      
      return textParts.map((part, i) => {
        const companyKey = companyNames.find(name => name.toLowerCase() === part.toLowerCase());
        if (companyKey && companyMap[companyKey]) {
          const company = companyMap[companyKey];
          return (
            <span key={i} className="inline-flex items-center gap-0.5">
              <img 
                src={company.logo} 
                alt={`${companyKey} Logo`} 
                className="w-4 h-4 object-contain inline-block"
              />
              <Link
                to={`/work#${company.slug}`}
                className="underline text-minimal-grey hover:text-minimal-red transition-colors"
              >
                {part}
              </Link>
            </span>
          );
        }
        return <span key={i}>{part}</span>;
      });
    }

    // Render parts with links
    const renderedParts: React.ReactNode[] = [];
    parts.forEach((part, i) => {
      if (part.type === 'link' && part.url) {
        // Check if it's an internal route or external URL
        if (part.url.startsWith('/')) {
          renderedParts.push(
            <Link
              key={i}
              to={part.url}
              className="underline text-minimal-grey hover:text-minimal-red transition-colors"
            >
              {part.content}
            </Link>
          );
        } else {
          renderedParts.push(
            <a
              key={i}
              href={part.url}
              target="_blank"
              rel="noopener noreferrer"
              className="underline text-minimal-grey hover:text-minimal-red transition-colors"
            >
              {part.content}
            </a>
          );
        }
      } else if (part.type === 'text') {
        // For text parts, check for companies
        const companyNames = Object.keys(companyMap).sort((a, b) => b.length - a.length);
        const companyPattern = new RegExp(`(${companyNames.map(name => name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'gi');
        const textParts = part.content.split(companyPattern);
        
        textParts.forEach((textPart, j) => {
          const companyKey = companyNames.find(name => name.toLowerCase() === textPart.toLowerCase());
          if (companyKey && companyMap[companyKey]) {
            const company = companyMap[companyKey];
            renderedParts.push(
              <span key={`${i}-${j}`} className="inline-flex items-center gap-0.5">
                <img 
                  src={company.logo} 
                  alt={`${companyKey} Logo`} 
                  className="w-4 h-4 object-contain inline-block"
                />
                <Link
                  to={`/work#${company.slug}`}
                  className="underline text-minimal-grey hover:text-minimal-red transition-colors"
                >
                  {textPart}
                </Link>
              </span>
            );
          } else if (textPart) {
            renderedParts.push(<span key={`${i}-${j}`}>{textPart}</span>);
          }
        });
      }
    });
    
    return renderedParts;
  };

  return (
    <main className="reading-page">
      <header className="work-nav">
        <Link to="/" className="work-nav__brand">Vividh Mahajan</Link>
        <nav aria-label="Writing page navigation">
          <Link to="/">Home</Link>
          <Link to="/work">Work</Link>
          <a href="https://github.com/Lasdw6" target="_blank" rel="noreferrer">GitHub</a>
        </nav>
      </header>

      <div className="reading-page__layout">
        {/* Rail version. Hidden on narrow screens, where the copy below the
            title carries the contents instead. */}
        <aside className="reading-page__toc">
          <nav aria-label="Table of contents">
            <ol>
              {tableOfContents.map(item => (
                <li key={item.id}>
                  <a href={`#${item.id}`}>{item.label}</a>
                </li>
              ))}
            </ol>
          </nav>
        </aside>

        <article className="reading-page__inner">
        <div className="space-y-3">
          <header className="reading-page__header">
            <h1>
              {post.title}
            </h1>
            <div className="reading-page__meta">
              <span>{formatDate(post.date)}</span>
              <span>·</span>
              <span>{readTime} min read</span>
            </div>
            {post.subtitle && (
              <p className="reading-page__subtitle">{post.subtitle}</p>
            )}
          </header>

          <div className="reading-page__toc reading-page__toc--inline">
            <nav aria-label="Table of contents">
              <ol>
                {tableOfContents.map(item => (
                  <li key={item.id}>
                    <a href={`#${item.id}`}>{item.label}</a>
                  </li>
                ))}
              </ol>
            </nav>
          </div>

          {post.heroImage && (
            <div className="reading-page__hero-breakout">
              <motion.figure
                className="reading-page__hero"
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.65, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
              >
                <img src={post.heroImage} alt={post.heroAlt || ''} />
                {post.heroCaption && (
                  <figcaption>{post.heroCaption}</figcaption>
                )}
              </motion.figure>
            </div>
          )}

          <div className="reading-page__content" id="introduction">
            <div>
              {contentSegments.map((segment, segmentIndex) => {
                if (segment.type === 'mermaid' && segment.id) {
                  return <MermaidDiagram key={segment.id} id={segment.id} definition={segment.content} description={segment.description} />;
                }
                
                // Render text content
                return segment.content.split('\n').map((line, lineIndex) => {
                  const trimmedLine = line.trim();
                  const key = `segment-${segmentIndex}-line-${lineIndex}`;
                  
                  // Empty line
                  if (!trimmedLine) {
                    return <div key={key} className="mb-0.5" />;
                  }

                  if (trimmedLine === '---') {
                    return <hr key={key} className="reading-page__outro-divider" />;
                  }

                  // Interactive session-replay demo
                  if (trimmedLine === '[REPLAY:stateless-demo]') {
                    return <SessionReplay key={key} trace={statelessDemoTrace} />;
                  }

                  // Think-vs-wake overlay figure
                  if (trimmedLine === '[FIGURE:think-wake]') {
                    return <ThinkWakeFigure key={key} />;
                  }

                  // Harness: MCP catalog vs CLI progressive discovery
                  if (trimmedLine === '[FIGURE:mcp-vs-cli]') {
                    return <McpVsCliFigure key={key} />;
                  }

                  // Harness: interactive Blender quickstart walkthrough
                  if (trimmedLine === '[FIGURE:blender-quickstart]') {
                    return <BlenderQuickstartFigure key={key} />;
                  }

                  // BoostOS: the application layer's control ceiling
                  if (trimmedLine === '[FIGURE:app-control-ceiling]') {
                    return <AppControlCeilingFigure key={key} />;
                  }

                  // BoostOS: agents represented as first-class system processes
                  if (trimmedLine === '[FIGURE:agent-process-manager]') {
                    return <AgentProcessFigure key={key} />;
                  }

                  // BoostOS: human-formatted system output versus structured output
                  if (trimmedLine === '[FIGURE:system-output]') {
                    return <SystemOutputFigure key={key} />;
                  }

                  if (trimmedLine === '[FIGURE:piper-compaction]') {
                    return <PiperCompactionFigure key={key} />;
                  }

                  if (trimmedLine === '[FIGURE:stateless-reliability]') {
                    return <StatelessReliabilityFigure key={key} />;
                  }

                  const diagramPlaceholderMatch = trimmedLine.match(/^\[DIAGRAM-PLACEHOLDER:([^\]]+)\]$/);
                  if (diagramPlaceholderMatch) {
                    return (
                      <figure key={key} className="diagram-placeholder">
                        <span>Diagram placeholder</span>
                        <figcaption>{diagramPlaceholderMatch[1]}</figcaption>
                      </figure>
                    );
                  }

                  const videoMatch = trimmedLine.match(/^\[VIDEO:([^\]|]+)(?:\|([^\]]+))?\]$/);
                  if (videoMatch) {
                    return (
                      <VideoFigure
                        key={key}
                        src={videoMatch[1]}
                        poster={videoMatch[1].replace(/\.[^.]+$/, '-poster.png')}
                        caption={videoMatch[2]}
                      />
                    );
                  }

                  const mediaPlaceholderMatch = trimmedLine.match(/^\[MEDIA-PLACEHOLDER:([^\]|]+)(?:\|([^\]]+))?\]$/);
                  if (mediaPlaceholderMatch) {
                    return (
                      <figure key={key} className="editorial-placeholder">
                        <span>Media placeholder</span>
                        <strong>{mediaPlaceholderMatch[1]}</strong>
                        {mediaPlaceholderMatch[2] && (
                          <figcaption>{mediaPlaceholderMatch[2]}</figcaption>
                        )}
                      </figure>
                    );
                  }

                  const xPostMatch = trimmedLine.match(/^\[XPOST:(https?:\/\/[^\]|]+)(?:\|([^\]]+))?\]$/);
                  if (xPostMatch) {
                    return <XPostEmbed key={key} url={xPostMatch[1]} caption={xPostMatch[2]} />;
                  }

                  const codeQuoteMatch = trimmedLine.match(/^\[CODEQUOTE:([^\]]+)\]$/);
                  if (codeQuoteMatch) {
                    return (
                      <pre key={key} className="reading-page__code-quote">
                        <code>{codeQuoteMatch[1]}</code>
                      </pre>
                    );
                  }

                  // Subsection header (###) — rendered but kept out of the contents list
                  if (trimmedLine.startsWith('###')) {
                    const subText = trimmedLine.substring(3).trim();
                    return (
                      <h3
                        key={key}
                        id={toHeadingId(subText)}
                        className="reading-page__subheading"
                      >
                        {subText}
                      </h3>
                    );
                  }

                  // Section header (starts with ##)
                  if (trimmedLine.startsWith('##')) {
                    const headerText = trimmedLine.substring(2).trim();
                    return (
                      <h2
                        key={key}
                        id={toHeadingId(headerText)}
                        className="text-xl font-medium text-minimal-grey mt-4 mb-2 first:mt-0"
                      >
                        {headerText}
                      </h2>
                    );
                  }
                  
                  // Bullet point
                  if (trimmedLine.startsWith('•')) {
                    return (
                      <div key={key} className="mb-1">
                        {renderTextWithLinks(trimmedLine)}
                      </div>
                    );
                  }
                  
                  // Regular paragraph text
                  const isOutroProjectLink =
                    trimmedLine.startsWith('Check out what I built-') ||
                    trimmedLine.startsWith('Code:');

                  return (
                    <p key={key} className={isOutroProjectLink ? 'reading-page__outro-link' : 'mb-3'}>
                      {renderTextWithLinks(trimmedLine)}
                    </p>
                  );
                });
              })}
            </div>
          </div>

        </div>
        </article>

        <div className="reading-page__balance" aria-hidden="true" />
      </div>
    </main>
  );
};

export default BlogDetail;
